import React, { useState } from 'react';

import { queryCache, useMutation } from 'react-query';
import { Toast } from 'src/common/components';

import { setFeatureStatus } from '../api';
import {
  CANDIDATES_QUERY_KEY,
  EDITION_QUERY_KEY,
  EDITIONS_QUERY_KEY,
  FEATURE_CATALOG_QUERY_KEY,
  FEATURE_STATUS_LABEL,
} from '../constants';
import { FeatureCatalogEntry, FeatureCatalogStatus } from '../types';
import { getFeatureFieldErrors } from '../utils';

const STATUS_OPTIONS = Object.keys(FEATURE_STATUS_LABEL) as FeatureCatalogStatus[];

// Changes the lifecycle status through set_status (which writes the audit log).
// Moving away from Active keeps the feature out of the digest and sends any ready-to-send
// edition that carries it back to Draft, so those lists are refreshed too.
const FeatureStatusControl: React.FC<{ entry: FeatureCatalogEntry }> = ({ entry }) => {
  const [status, setStatus] = useState<FeatureCatalogStatus>(entry.status);
  const [reason, setReason] = useState('');

  const [save, { isLoading }] = useMutation(
    (next: FeatureCatalogStatus) => setFeatureStatus(entry.id, next, reason.trim()),
    {
      onSuccess: (_data, saved) => {
        setReason('');
        [
          FEATURE_CATALOG_QUERY_KEY,
          EDITIONS_QUERY_KEY,
          EDITION_QUERY_KEY,
          CANDIDATES_QUERY_KEY,
        ].forEach((key) => void queryCache.invalidateQueries(key));
        Toast.success(`Estado actualizado a ${FEATURE_STATUS_LABEL[saved]}.`);
      },
      onError: (error) => {
        const { detail, status: statusError } = getFeatureFieldErrors(error);
        Toast.error(detail || statusError || 'No se pudo cambiar el estado.');
      },
    }
  );

  return (
    <div className="rounded-xl bg-gray-50 p-4 mb-4 border border-gray-200">
      <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
        Estado
        <select
          className="input mt-0.5 w-full text-sm"
          value={status}
          disabled={isLoading}
          onChange={(e) => setStatus(e.target.value as FeatureCatalogStatus)}
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {FEATURE_STATUS_LABEL[option]}
            </option>
          ))}
        </select>
      </label>
      <label className="block mt-2 text-xs font-medium tracking-wide text-gray-500 uppercase">
        Motivo (opcional)
        <input
          className="input mt-0.5 w-full text-sm"
          value={reason}
          disabled={isLoading}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      {status !== 'active' && (
        <p className="text-amber-800 mt-2 text-xs">
          Un feature {FEATURE_STATUS_LABEL[status]} no sale en el digest. Si está en una edición
          lista para enviar, esa edición vuelve a Borrador.
        </p>
      )}
      <button
        type="button"
        className="btn btn-primary mt-3 text-sm"
        disabled={isLoading || status === entry.status}
        onClick={() => save(status)}
      >
        Guardar estado
      </button>
    </div>
  );
};

export default FeatureStatusControl;
